import MaskedTextField from '@/common/components/MaskedTextField'
import { timeMask } from '@/common/constants/masks.constant'
import { useState } from 'react'
import { Control, Controller, Path } from 'react-hook-form'
import TimeOptionsPopover from './components/TimePopover'

export interface TimeInputProps<T extends object> {
  fieldName: Path<T>
  control: Control<T>
  label?: string
  markAsRequired?: boolean
  className?: string
  disabled?: boolean
  placeholder?: string
  errorMessage?: string
  showTimeSelector?: boolean
  handleChange?: (value: string) => void
}

const TimeInput = <T extends object>({
  fieldName,
  control,
  label,
  markAsRequired = false,
  className,
  disabled = false,
  placeholder = 'HH:MM',
  errorMessage,
  showTimeSelector = true,
  handleChange,
}: TimeInputProps<T>) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [selectedHour, setSelectedHour] = useState<string | undefined>()

  const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'))
  const minutes = ['00', '15', '30', '45']

  const parseHour = (value: string) => {
    const hour = value?.split(':')[0]
    return hour
  }

  const parseMinute = (value: string) => {
    const minute = value?.split(':')[1]
    return minute
  }

  return (
    <Controller
      control={control}
      name={fieldName}
      render={({ field }) => {
        const currentHour = parseHour(field.value || '')
        const currentMinute = parseMinute(field.value || '')
        const activeHour = selectedHour ?? currentHour

        const handleMinuteSelect = (minute: string) => {
          if (!activeHour) return

          const newValue = `${activeHour}:${minute}`

          field.onChange(newValue)
          handleChange?.(newValue)

          setSelectedHour(undefined)
          setIsPopoverOpen(false)
        }

        const handleHourSelect = (hour: string) => {
          setSelectedHour(hour)

          const newValue = currentMinute ? `${hour}:${currentMinute}` : `${hour}:00`

          field.onChange(newValue)
          handleChange?.(newValue)
        }

        return (
          <MaskedTextField
            LeftIcon={
              showTimeSelector ? (
                <TimeOptionsPopover
                  fieldName={fieldName}
                  isPopoverOpen={isPopoverOpen}
                  setIsPopoverOpen={setIsPopoverOpen}
                  setSelectedHour={setSelectedHour}
                  hours={hours}
                  minutes={minutes}
                  disabled={disabled}
                  activeHour={activeHour}
                  currentMinute={currentMinute}
                  handleMinuteSelect={handleMinuteSelect}
                  onHourSelect={handleHourSelect}
                />
              ) : undefined
            }
            label={label}
            markAsRequired={markAsRequired}
            mask={timeMask}
            placeholder={placeholder}
            className={className}
            errorMessage={errorMessage}
            name={fieldName}
            value={field.value || ''}
            onChange={(e) => field.onChange(e.target.value)}
            disabled={disabled}
            onFocus={() => setIsPopoverOpen(true)}
          />
        )
      }}
    />
  )
}

export default TimeInput
